import { useContext, useEffect, useRef, useState } from "react"

// ** Gatsby
import { GatsbyImage, StaticImage } from "gatsby-plugin-image"

// ** Custom Hooks
import { useHomePageData } from "../../hooks/use-home-page-data"

// ** Custom Components
import Box from "../../components/Box"
import Button from "../../components/Button"
import CardContainer from "../../components/CardContainer"
import Container from "../../components/Container"
import Heading from "../../components/Heading"
import Rating from "../../components/Rating"
import SwiperSlider from "../../components/SwiperSlider"
import Text from "../../components/Text"
import i18n from "../../data/i18n_constants"

// ** Utils
import { classNames } from "../../utils/helpers"
import { useWindowDimensions } from "../../utils/helpers"
import PageContext from "../../contexts/PageContext"
import { useAwardsReviewsData } from "../../hooks/use-awards-reviews-data"
import { useGoogleReviewData } from "../../hooks/use-google-reviews"

const SectionGoogleReviews = ({ section }: any) => {
  const { title, subTitle, listItems, tags } = section
  const homePageData = useHomePageData()
  const awardsReviewsData = useAwardsReviewsData()
  const googleReviewsData = useGoogleReviewData()
  const textRef = useRef<HTMLParagraphElement>(null)
  const [isClamped, setIsClamped] = useState(false)
  const [showAll, setShowAll] = useState<number | null>(null)
  const { width } = useWindowDimensions()
  const page = useContext(PageContext)
  const lang = page.i18n_lang
  const noAwards = tags.includes("no-awards")

  useEffect(() => {
    if (!textRef.current) return
    const text = textRef.current
    const isClamped = text.scrollHeight > text.clientHeight
    setIsClamped(isClamped)
  }, [textRef, width])

  const renderReviews = (reviews: any) => {
    return reviews.map((item: any, idx: any) => (
      <CardContainer
        key={idx}
        forceHeight={false}
        className="lg:flex-1 bg-white h-full"
        header={
          <Box className="flex justify-between gap-2 items-center flex-1 px-4 pt-4">
            <Box className="flex items-center gap-x-1">
              <Box className="w-11 h-11 overflow-hidden rounded-full">
                <StaticImage
                  objectFit="contain"
                  className="w-full h-full"
                  placeholder="blurred"
                  src="../../images/google.webp"
                  alt="8Twelve Mortgage"
                  quality={100}
                />
              </Box>
              <Heading as="h6" component="span" className="text-blue-500">
                {item.author_name || item.title}
              </Heading>
            </Box>
            <Box>
              <Rating fillColor="text-blue-500" value={5} />
            </Box>
          </Box>
        }
      >
        <Text
          className={classNames(
            showAll !== idx && "line-clamp-5 lg:line-clamp-3 lg:inline"
          )}
          ref={textRef}
        >
          {item.text || item.review}
        </Text>
        {isClamped && (
          <Button
            className="px-0 w-max py-0 lg:inline"
            onClick={() => setShowAll(showAll === idx ? null : idx)}
          >
            {showAll === idx ? i18n.read_less[lang] : i18n.read_more[lang]}
          </Button>
        )}
      </CardContainer>
    ))
  }

  return (
    <Container constrainedToBreakPoint className="px-0">
      <Box
        className={`grid text-center justify-center max-w-screen mx-auto gap-y-4 ${
          !noAwards ? "lg:grid-cols-2" : "lg:grid-cols-1"
        } lg:items-center`}
      >
        <Box className="flex items-center lg:col-start-2 lg:row-start-1 justify-center lg:justify-end">
          <Box className="w-[128px] lg:w-[242px] h-[128px] lg:h-[242px] relative">
            <Box className="absolute top-15 left-33 font-extrabold fluid-4xl block text-blue-500 mb-2 lg:mb-6  z-10">
              {awardsReviewsData.sanityAwardsReview.rating}
            </Box>
            <GatsbyImage
              className="w-full h-full"
              alt={homePageData?.googleReview?.nodes[0]?.label || ""}
              image={
                awardsReviewsData.sanityAwardsReview.ratingBgimage.asset
                  .gatsbyImageData
              }
            />
            <Box className="absolute bottom-5 text-gray-500 w-full z-10 block text-center font-bold text-[8px] lg:text-base">
              {awardsReviewsData.sanityAwardsReview.numberReviews}
            </Box>
          </Box>
          {!noAwards && (
            <Box>
              <SwiperSlider
                pagination={false}
                navigation={false}
                autoplay={{
                  delay: 5000,
                }}
                speed={700}
                className="[&>.swiper-wrapper]:p-0 w-[128px] lg:w-[242px] h-[128px] lg:h-[242px]"
              >
                {awardsReviewsData.sanityAwardsReview.awards.length > 0 &&
                  awardsReviewsData.sanityAwardsReview.awards.map(
                    (award: any, idx: any) => (
                      <GatsbyImage
                        key={idx}
                        className="w-full h-full"
                        objectFit="contain"
                        image={award.asset.gatsbyImageData}
                        alt={award.label || ""}
                      />
                    )
                  )}
              </SwiperSlider>
            </Box>
          )}
        </Box>
        <Box className="basis-1/2 md:px-20">
          <Heading as="h2" className="text-blue-500 mb-2 lg:mb-6">
            {title}
          </Heading>
          <Text>{subTitle}</Text>
        </Box>
      </Box>
      <Heading as="h6" className="mt-10 text-center mb-4 lg:hidden">
        Google Reviews
      </Heading>

      <Box className="w-screen lg:w-full px-3 lg:px-0">
        <SwiperSlider
          className="mt-8 lg:mt-12"
          childClassName="rounded-lg max-w-[85%]"
          slidesPerView="auto"
          breakpoints={{
            768: {
              slidesPerView: 2,
            },
            1024: {
              slidesPerView: 3,
            },
          }}
          navigation={false}
        >
          {!awardsReviewsData.sanityAwardsReview.showReviews
            ? renderReviews(
                googleReviewsData.allGooglePlacesPlace.nodes[0].childrenGooglePlacesReview.slice(
                  0,
                  3
                )
              )
            : renderReviews(awardsReviewsData.sanityAwardsReview.reviews)}
        </SwiperSlider>
      </Box>
    </Container>
  )
}

export default SectionGoogleReviews
